// [UNTAPPED-v2] Smart Table - Untapped Opportunities (Main Frontend Component)
// Decoupled validation: POST /validate-all → background job, GET /progress → polling, POST /results → cache display
// Tables: affiliate_clean (source) + untapped_validation_cache (cache)
// Controllers: validationJobManager.js (background), streamingController.js (cache reads)
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Loader2,
  Globe,
  X,
  Flag,
  TrendingUp,
  MousePointer,
  ExternalLink,
  CheckCircle,
  AlertCircle,
  Sparkles,
  ArrowLeft,
  Info,
  XCircle,
  Layers
} from 'lucide-react';
import UniversalTable from '../../services/universalTable';
import { CountryFlag } from '../../services/countryFlagService';
import { countryList, getCountryName } from '../../services/countryList';

// API BASE PATH
const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

// Progress Bar Component with real validation progress from background job
const ValidationProgressBar = ({ progress, message, validationInfo }) => {
  const approved = validationInfo?.approved || 0;
  const validated = validationInfo?.validated || 0;
  const total = validationInfo?.totalCandidates || 0;

  return (
    <div className="flex flex-col items-center gap-6 p-8">
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg">
        <Sparkles className="w-6 h-6 text-white animate-pulse" />
      </div>
      <div className="w-96 bg-gray-100 dark:bg-slate-800 rounded-full h-2 overflow-hidden">
        <div
          className="bg-gradient-to-r from-emerald-500 via-teal-500 to-emerald-600 h-2 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${Math.max(progress, 2)}%` }}
        />
      </div>
      <div className="text-center space-y-2">
        <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 block">{Math.round(progress)}%</span>
        <span className="text-base text-gray-700 dark:text-slate-200 font-medium block">{message}</span>
        {validated > 0 && (
          <span className="text-sm text-emerald-600 dark:text-emerald-400 block font-medium">
            Validated {validated}/{total} — Found {approved} relevant
          </span>
        )}
      </div>
    </div>
  );
};

const SmartTable = () => {
  const navigate = useNavigate();

  // Get brand data from Redux store
  const selectedBrand = useSelector(state => state.untappedOpportunities.selectedBrand);
  const user = useSelector(state => state.auth.user);
  const brandData = selectedBrand || {};

  const [affiliates, setAffiliates] = useState([]);
  const [loading, setLoading] = useState(false); // Don't auto-load - wait for user to click
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingMessage, setLoadingMessage] = useState('Initializing...');
  const [error, setError] = useState(null);
  const [hasStartedSearch, setHasStartedSearch] = useState(false); // Track if user started search
  const hasStartedSearchRef = useRef(false); // Ref to avoid stale closure in handlers

  // Country selection state - user selects one country at a time for faster queries
  const [selectedCountry, setSelectedCountry] = useState('');

  // Vertical selection state - user can switch between brand's verticals
  const [selectedVertical, setSelectedVertical] = useState('');

  // Sub-category selection state. A vertical says which PRODUCT ('Trading &
  // Investment'); a sub-category says which instruments inside it (Forex, ETFs,
  // Crypto...). Only Trading & Investment brands carry any, and '' means "all
  // of them" — the pool the card's Untapped count is drawn from.
  const [selectedSubVertical, setSelectedSubVertical] = useState('');

  // Validation job state (decoupled: validate in background, display from cache)
  const [validationJobStatus, setValidationJobStatus] = useState(null);
  // { totalCandidates, validated, approved, rejected, isComplete, isRunning, isPaused, hasMoreCandidates, approvedTarget }
  const validationPollRef = useRef(null);

  // Chunked validation: how many approved to find before pausing (auto-loads +50 when user approaches boundary)
  const [approvedTarget, setApprovedTarget] = useState(70);

  // Request ID to prevent stale responses
  const currentRequestIdRef = useRef(0);

  // Pagination state - fixed page size of 10
  const [currentPage, setCurrentPage] = useState(0);
  const currentPageRef = useRef(0);
  const PAGE_SIZE = 10; // Fixed page size - backend always returns 10 validated results
  const [totalItems, setTotalItems] = useState(0);
  const [totalValidated, setTotalValidated] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [isLoadingTableData, setIsLoadingTableData] = useState(false);
  const [paginationLoadingMessage, setPaginationLoadingMessage] = useState('');

  // Keep ref in sync so polling closure has latest page
  useEffect(() => { currentPageRef.current = currentPage; }, [currentPage]);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');

  // "Already Approached" filter: 'No' (the default) hides the affiliates this
  // person marked approached or emailed, 'Yes' shows only them. The ref keeps
  // the polling closures on the current choice.
  const [alreadyApproached, setAlreadyApproached] = useState('No');
  const alreadyApproachedRef = useRef('No');

  // Get available countries from brand data (handles both string and array formats)
  const availableCountries = useMemo(() => {
    if (brandData.countries) {
      // Handle string format: "DE,AT,PL,AU,CA,IT,AE"
      if (typeof brandData.countries === 'string' && brandData.countries.length > 0) {
        return brandData.countries.split(',').map(c => c.trim()).filter(c => c.length > 0);
      }
      // Handle array format
      if (Array.isArray(brandData.countries) && brandData.countries.length > 0) {
        return brandData.countries.flatMap(c => c.split(',')).map(c => c.trim()).filter(c => c.length > 0);
      }
    }
    if (brandData.country) {
      return [brandData.country];
    }
    return [];
  }, [brandData.countries, brandData.country]);

  // Get available verticals from brand data (handles comma-separated string)
  const availableVerticals = useMemo(() => {
    if (brandData.vertical) {
      return brandData.vertical.split(',').map(v => v.trim()).filter(v => v.length > 0);
    }
    return [];
  }, [brandData.vertical]);

  // The sub-categories this brand was added with (comma-joined on the brand row,
  // empty for every gambling brand).
  const availableSubVerticals = useMemo(() => {
    if (brandData.sub_vertical) {
      return brandData.sub_vertical.split(',').map(v => v.trim()).filter(v => v.length > 0);
    }
    return [];
  }, [brandData.sub_vertical]);

  // What the request carries: [] when "All" is selected, so the pool stays the
  // brand's whole vertical and nothing changes for gambling brands.
  const subVerticalsToQuery = useMemo(
    () => (selectedSubVertical ? [selectedSubVertical] : []),
    [selectedSubVertical]
  );

  // Set default selected country when available countries change (don't auto-start search)
  useEffect(() => {
    if (availableCountries.length > 0 && !selectedCountry) {
      const firstCountry = availableCountries[0];
      setSelectedCountry(firstCountry);
    }
  }, [availableCountries, selectedCountry]);

  // Set default selected vertical when available verticals change
  useEffect(() => {
    if (availableVerticals.length > 0 && !selectedVertical) {
      setSelectedVertical(availableVerticals[0]);
    }
  }, [availableVerticals, selectedVertical]);

  // Get user plan
  const userPlan = user?.plan || 'Free';

  // Cache ref to store data per brand - prevents re-fetching when navigating back
  const dataCache = useRef(new Map());

  // Generate cache key based on brand, country, and page
  const getCacheKey = useCallback((brandUrl, country, page, pageSize, search, vertical, subVerticals) => {
    const subKey = (subVerticals || []).join('+');
    return `${brandUrl}_${country || ''}_${vertical || ''}_${subKey}_${page}_${pageSize}_${search || ''}`;
  }, []);

  // Parse serp_validated_geos JSONB data into structured format
  // Format: "found,url|type:siteType|relevant:bool|reason:text|confidence:num"
  // Only includes entries for the selected country (prevents e.g. UAE pages showing for PL)
  const parseSerpValidatedGeos = (serpValidatedGeos) => {
    if (!serpValidatedGeos || typeof serpValidatedGeos !== 'object') return [];

    const parsedPages = [];
    const sc = selectedCountry?.toUpperCase();

    for (const [geoKey, validationString] of Object.entries(serpValidatedGeos)) {
      if (!validationString || typeof validationString !== 'string') continue;
      // Keys can be "GB" (old) or "GB:casino" (new) — extract the country code part
      const countryCode = geoKey.split(':')[0].toUpperCase();
      // Only parse entries matching the selected country
      if (sc && countryCode !== sc) continue;

      const parts = validationString.split(',');
      const isFound = parts[0] === 'true';

      if (!isFound) continue; // Skip not found entries

      // Get URL and metadata
      const urlAndMetadata = parts.slice(1).join(',');
      const [url, ...metadataParts] = urlAndMetadata.split('|');

      if (!url || !url.startsWith('http')) continue;

      // Parse metadata
      let siteType = 'unknown';
      let isRelevant = false;
      let reason = '';
      let confidence = 0;

      for (const part of metadataParts) {
        if (part.startsWith('type:')) {
          siteType = part.replace('type:', '');
        } else if (part.startsWith('relevant:')) {
          isRelevant = part.replace('relevant:', '') === 'true';
        } else if (part.startsWith('reason:')) {
          reason = part.replace('reason:', '');
        } else if (part.startsWith('confidence:')) {
          confidence = parseFloat(part.replace('confidence:', '')) || 0;
        }
      }

      parsedPages.push({
        countryCode,
        url,
        siteType,
        isRelevant,
        reason,
        confidence,
        isAffiliate: siteType === 'affiliate',
        relevantCountries: isRelevant ? [countryCode] : []
      });
    }

    // Sort by confidence (highest first), then by relevant status
    return parsedPages.sort((a, b) => {
      if (a.isRelevant !== b.isRelevant) return b.isRelevant ? 1 : -1;
      return b.confidence - a.confidence;
    });
  };

  // Transform function to match table structure
  const TransformDataAPI = (data) => {
    const transformedData = data.map((item, index) => {
      // Parse serp_validated_geos to get scanned pages with relevance info
      const serpValidatedGeos = item.serp_validated_geos || {};
      const parsedScannedPages = parseSerpValidatedGeos(serpValidatedGeos);

      // Priority: 1. scanned_pages_json from DB (per-country+vertical), 2. scanned_pages from validation, 3. parsed from serp_validated_geos
      // scanned_pages_json format: { "US:casino": [...pages], "US:sports": [...pages] } (new) or { "US": [...] } (old) or [...] (legacy)
      const sc = selectedCountry?.toUpperCase();
      const sv = (selectedVertical || brandData.vertical || 'casino').toLowerCase().replace(/[_\s]+/g, '').trim();
      const getCountryPages = (spJson) => {
        if (!spJson) return [];
        // New format: object keyed by country:vertical or country
        if (spJson && !Array.isArray(spJson) && typeof spJson === 'object') {
          // Try vertical-specific key first (e.g. "US:casino")
          if (sc && sv && spJson[`${sc}:${sv}`]) return spJson[`${sc}:${sv}`];
          // Fallback: old country-only key (e.g. "US") — only for casino (old data was casino)
          if (sc && sv === 'casino' && spJson[sc]) return spJson[sc];
          // Fallback: any available vertical for the same country (e.g. "GB:casino" when searching lottery)
          // Better to show pages from another vertical than show nothing
          if (sc) {
            for (const key of Object.keys(spJson)) {
              if (key.startsWith(`${sc}:`) && Array.isArray(spJson[key]) && spJson[key].length > 0) {
                return spJson[key];
              }
            }
          }
          return [];
        }
        // Legacy format: flat array — filter by relevantCountries
        if (Array.isArray(spJson) && spJson.length > 0) {
          if (!sc) return spJson;
          const filtered = spJson.filter(p => {
            if (!p.relevantCountries || p.relevantCountries.length === 0) return true;
            return p.relevantCountries.some(rc => rc.toUpperCase() === sc);
          });
          return filtered;
        }
        return [];
      };
      const dbScannedPages = getCountryPages(item.scanned_pages_json);
      let scannedPages = dbScannedPages.length > 0
        ? dbScannedPages
        : item.scanned_pages?.length > 0
          ? item.scanned_pages
          : parsedScannedPages;

      // Fallback: if no scanned pages from any source but we have a validated_page URL,
      // create a synthetic entry so the AI-verified page shows with a green checkmark
      if (scannedPages.length === 0) {
        const validatedUrl = item.validated_page || item.validated_page_url || item.affiliate_url_confidence?.split(',')[0] || '';
        if (validatedUrl && validatedUrl.startsWith('http')) {
          scannedPages = [{
            url: validatedUrl,
            siteType: item.site_type || 'affiliate',
            isAffiliate: true,
            isRelevant: true,
            relevantCountries: item.relevant_countries || [sc],
            confidence: item.relevancy_score || item.validation_confidence || 0.8,
            reason: 'AI verified page'
          }];
        }
      }

      return {
        paid_traffic: item.adwords_traffic,
        imageUrl: item.screenshot_url || 'blurrow.png',
        paid_traffic_difference: item.adwords_traffic_difference,
        affiliate_url: item.affiliate_url,
        affiliate_url_confidence: item.affiliate_url_confidence,
        validated_page: item.validated_page || item.affiliate_url_confidence?.split(',')[0] || '',
        site_type: item.site_type || 'affiliate',
        is_relevant: item.is_relevant !== false,
        content_source: item.content_source || '',
        already_approached: item.already_approached,
        email: item.email,
        id: item.id || `affiliate-${index}`,
        keywords: item.keywords,
        organic_traffic: item.organic_traffic,
        organic_traffic_difference: item.organic_traffic_difference,
        paid_keyword: item.paid_keyword,
        adwords_keywords: item.adwords_keywords,
        paid_top_page: item.paid_top_page,
        rank: item.rank,
        rank_difference: item.rank_difference,
        top_geo_organic_traffic: item.top_geo_organic_traffic,
        top_geo_organic_traffic_difference: item.top_geo_organic_traffic_difference,
        top_page: item.top_page,
        total_traffic: item.total_traffic,
        total_traffic_difference: item.total_traffic_difference,
        screenshot_url: item.screenshot_url,
        organic_keywords: item.organic_keywords,
        organic_cost: item.organic_cost,
        adwords_cost: item.adwords_cost,
        top_geos: item.top_geos,
        top_geo_rank: item.top_geo_rank,
        vertical: item.vertical,
        sub_vertical: item.sub_vertical,
        existing_affiliate: item.existing_affiliate,
        country_traffic: item.country_traffic || item.country_specific_traffic,
        validation_reason: item.validation_reason || '',
        validation_confidence: item.validation_confidence || 0,
        relevant_countries: item.relevant_countries || [],
        scanned_pages: scannedPages,
        serp_validated_geos: serpValidatedGeos,
        priority_tier: item.priority_tier ?? item._fp_tier ?? 99
      };
    });
    return transformedData;
  };

  // Fetch validated results from cache-only endpoint (instant, no validation)
  // POST /api/serp-validated-opportunities/results — pure DB read
  const fetchResults = async (page = 0, search = '', countryOverride = null, verticalOverride = null, silent = false) => {
    if (!brandData.brand_url) {
      setError('No brand selected. Please select a brand from the directory.');
      return;
    }

    const thisRequestId = ++currentRequestIdRef.current;

    const countryToQuery = countryOverride || selectedCountry || (availableCountries.length > 0 ? availableCountries[0] : '');
    const verticalToQuery = verticalOverride || selectedVertical || brandData.vertical;

    // Check frontend cache first
    const approachedToQuery = alreadyApproachedRef.current;
    const cacheKey = `${getCacheKey(brandData.brand_url, countryToQuery, page, PAGE_SIZE, search, verticalToQuery, subVerticalsToQuery)}_${approachedToQuery}`;
    if (dataCache.current.has(cacheKey)) {
      const cachedData = dataCache.current.get(cacheKey);
      setAffiliates(cachedData.affiliates);
      setTotalItems(cachedData.totalItems);
      setTotalValidated(cachedData.totalValidated);
      setTotalPages(cachedData.totalPages);
      setCurrentPage(cachedData.currentPage);
      setIsLoadingTableData(false);
      return;
    }

    if (!silent) setIsLoadingTableData(true);
    setError(null);

    try {
      const response = await fetch(`${basePath}/api/serp-validated-opportunities/results`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brand_url: brandData.brand_url,
          brand_id: brandData.brand_id,
          vertical: verticalToQuery,
          sub_vertical: subVerticalsToQuery,
          countries: countryToQuery ? [countryToQuery] : [],
          page,
          pageSize: PAGE_SIZE,
          search: search || searchQuery,
          alreadyApproached: approachedToQuery
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const responseData = await response.json();

      // Skip if superseded by a newer request
      if (currentRequestIdRef.current !== thisRequestId) return;

      if (responseData.success) {
        const affiliatesData = responseData.data || [];
        setTotalItems(responseData.total || 0);
        setTotalValidated(responseData.totalApproved || 0);
        setTotalPages(responseData.totalPages || 0);
        setCurrentPage(page);

        const transformedData = TransformDataAPI(affiliatesData);

        // Save to frontend cache
        dataCache.current.set(cacheKey, {
          affiliates: transformedData,
          totalItems: responseData.total || 0,
          totalValidated: responseData.totalApproved || 0,
          totalPages: responseData.totalPages || 0,
          currentPage: page,
          timestamp: Date.now()
        });

        setAffiliates(transformedData);
      }

      setIsLoadingTableData(false);

    } catch (err) {
      if (currentRequestIdRef.current !== thisRequestId) return;
      console.error('Error fetching results:', err);
      setError(err.message || 'Failed to fetch results');
      setIsLoadingTableData(false);
    }
  };


  // Trigger background validation and start progress polling
  // POST /api/serp-validated-opportunities/validate-all → returns immediately
  // Then polls GET /progress every 3s until complete or paused
  // @param {number} targetOverride - approved target override (for auto-load on page boundary)
  // @param {boolean} isResume - true when resuming a paused job (don't clear results)
  const triggerValidation = async (countryOverride = null, verticalOverride = null, targetOverride = null, isResume = false) => {
    if (!brandData.brand_url) {
      setError('No brand selected. Please select a brand from the directory.');
      return;
    }

    const countryToQuery = countryOverride || selectedCountry || (availableCountries.length > 0 ? availableCountries[0] : '');
    const verticalToQuery = verticalOverride || selectedVertical || brandData.vertical;
    const target = targetOverride || approvedTarget || 70;

    if (!isResume) {
      // Fresh search — clear old state
      setAffiliates([]);
      setCurrentPage(0);
      setApprovedTarget(70);
    }
    setError(null);
    setLoading(!isResume); // Don't show full-screen loader on resume
    setLoadingProgress(0);
    setLoadingMessage(isResume ? 'Finding more relevant affiliates...' : 'Starting validation...');
    setValidationJobStatus(null);
    dataCache.current.clear();

    // Stop any existing polling
    if (validationPollRef.current) {
      clearInterval(validationPollRef.current);
      validationPollRef.current = null;
    }

    try {
      // POST /validate-all — triggers background validation, returns immediately
      const response = await fetch(`${basePath}/api/serp-validated-opportunities/validate-all`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          brand_url: brandData.brand_url,
          brand_id: brandData.brand_id,
          vertical: verticalToQuery,
          sub_vertical: subVerticalsToQuery,
          countries: countryToQuery ? [countryToQuery] : [],
          approvedTarget: target
        })
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const data = await response.json();

      if (!data.success) throw new Error(data.error || 'Failed to start validation');

      setValidationJobStatus(data);

      // If already complete (all candidates previously validated), skip polling
      if (data.isComplete) {
        setLoadingProgress(100);
        setLoadingMessage('All candidates already validated. Loading results...');
        setLoading(false);
        fetchResults(0, '', countryOverride, verticalOverride);
        return;
      }

      // If already paused (enough approved from previous runs), skip polling
      if (data.isPaused) {
        setLoading(false);
        fetchResults(0, '', countryOverride, verticalOverride);
        return;
      }

      // Start progress polling
      startProgressPolling(countryToQuery, verticalToQuery, countryOverride, verticalOverride);

    } catch (err) {
      console.error('Error starting validation:', err);
      setError(err.message || 'Failed to start validation');
      setLoading(false);
    }
  };

  // Poll GET /progress every 3 seconds for real-time validation updates
  const startProgressPolling = (countryToQuery, verticalToQuery, countryOverride, verticalOverride) => {
    // Track whether we've already transitioned to showing results
    let hasShownResults = false;
    let lastApprovedCount = 0;

    const pollProgress = async () => {
      try {
        const params = new URLSearchParams({
          brand_url: brandData.brand_url,
          countries: countryToQuery,
          vertical: verticalToQuery,
          // Comma-joined; empty means the whole vertical, matching the job key
          // /validate-all just started.
          sub_vertical: subVerticalsToQuery.join(',')
        });

        const response = await fetch(`${basePath}/api/serp-validated-opportunities/progress?${params}`, {
          credentials: 'include'
        });
        if (!response.ok) return;
        const data = await response.json();
        if (!data.success) return;

        setValidationJobStatus(data);

        // Update progress bar
        const progress = data.totalCandidates > 0
          ? Math.round((data.validated / data.totalCandidates) * 100)
          : 0;
        setLoadingProgress(progress);

        if (data.validated > 0) {
          setLoadingMessage(`Validated ${data.validated}/${data.totalCandidates} — Found ${data.approved} relevant`);
        } else {
          setLoadingMessage('Analyzing affiliate websites...');
        }

        // Show results as soon as we have enough approved for page 1 (or validation is done)
        if ((data.approved >= PAGE_SIZE || data.isComplete || data.isPaused) && !hasShownResults) {
          hasShownResults = true;
          setLoading(false);
          fetchResults(0, '', countryOverride, verticalOverride);
          lastApprovedCount = data.approved;
        }

        // Live update: when new approved results found, silently refresh current page in-place
        // User stays on same page — bar moves down as more relevant results appear
        if (hasShownResults && data.approved > lastApprovedCount) {
          lastApprovedCount = data.approved;
          dataCache.current.clear();
          fetchResults(currentPageRef.current, searchQuery, countryOverride, verticalOverride, true);
        }

        // Stop polling when validation is complete OR paused (found enough approved)
        if (data.isComplete || data.isPaused) {
          if (validationPollRef.current) {
            clearInterval(validationPollRef.current);
            validationPollRef.current = null;
          }
          dataCache.current.clear();
          // Final refresh on current page — silent, no spinner
          if (hasShownResults) {
            fetchResults(currentPageRef.current, searchQuery, countryOverride, verticalOverride, true);
          }
        }
      } catch (err) {
        console.error('Error polling progress:', err);
      }
    };

    // Poll immediately, then every 3 seconds
    pollProgress();
    validationPollRef.current = setInterval(pollProgress, 3000);
  };

  // Handle page change — instant cache read from POST /results
  // Auto-triggers more validation when user approaches the boundary (page before stripe)
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    fetchResults(newPage, searchQuery);

    // Auto-trigger: when user reaches the page before the stripe, load more
    if (validationJobStatus?.isPaused && validationJobStatus?.hasMoreCandidates) {
      const approved = validationJobStatus.approved || 0;
      const stripePage = Math.floor(approved / PAGE_SIZE); // page where stripe starts
      if (newPage >= stripePage - 1) {
        const newTarget = (approvedTarget || 70) + 50;
        setApprovedTarget(newTarget);
        triggerValidation(null, null, newTarget, true);
      }
    }
  };

  // Handle search — re-triggers validation for new search query
  const handleSearch = (query) => {
    setSearchQuery(query);
    setCurrentPage(0);
    dataCache.current.clear();
    triggerValidation();
  };

  // Reset state when brand changes — DON'T auto-fetch, wait for user to click button
  useEffect(() => {
    setAffiliates([]);
    setHasStartedSearch(false);
    hasStartedSearchRef.current = false;
    setError(null);
    setValidationJobStatus(null);
    setApprovedTarget(70);
    setSelectedSubVertical('');
    dataCache.current.clear();
    if (validationPollRef.current) {
      clearInterval(validationPollRef.current);
      validationPollRef.current = null;
    }
  }, [brandData.brand_url, brandData.brand_name, brandData.brand_id, brandData.vertical, brandData.sub_vertical]);

  // Handler to start the search when user clicks the button
  // Triggers background validation + progress polling
  const handleStartSearch = () => {
    if (selectedCountry) {
      prevHasStartedSearch.current = true;
      hasStartedSearchRef.current = true;
      setHasStartedSearch(true);
      triggerValidation();
    }
  };

  // Admin-only: Clear cache and re-scan from scratch
  const handleRescan = async () => {
    if (!selectedCountry || !brandData.brand_url) return;
    if (!window.confirm(`This will clear cached results and re-scan all affiliates for ${brandData.brand_url} in ${selectedCountry}. Continue?`)) return;

    try {
      setLoading(true);
      setLoadingMessage('Clearing cache...');

      // Step 1: Clear cache for this country/vertical
      await fetch(`${basePath}/api/serp-validated-opportunities/cache`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          country_code: selectedCountry,
          vertical: selectedVertical || brandData.vertical
        })
      });

      // Step 2: Start fresh validation
      prevHasStartedSearch.current = true;
      hasStartedSearchRef.current = true;
      setHasStartedSearch(true);
      setAffiliates([]);
      setCurrentPage(0);
      setApprovedTarget(70);
      triggerValidation();
    } catch (err) {
      console.error('Re-scan failed:', err);
      setError('Re-scan failed: ' + err.message);
      setLoading(false);
    }
  };

  // Handler for country change — triggers validation if search has already started
  const handleCountryChange = (newCountry) => {
    setSelectedCountry(newCountry);
    setCurrentPage(0);
    setApprovedTarget(70); // Reset to first chunk for new country
    if (hasStartedSearchRef.current && newCountry) {
      triggerValidation(newCountry);
    }
  };

  // Handler for vertical change - only updates selection, waits for user to click "Find Opportunities"
  const handleVerticalChange = (newVertical) => {
    setSelectedVertical(newVertical);
    setCurrentPage(0);
  };

  // Same contract as the vertical: pick, then click "Find Opportunities". The
  // cached results are keyed by sub-category too, so switching back to a
  // sub-category already searched is instant.
  const handleSubVerticalChange = (newSubVertical) => {
    setSelectedSubVertical(newSubVertical);
    setCurrentPage(0);
    setApprovedTarget(70);
  };

  // Already Approached filter: reads the cache again at once (no new validation).
  const handleAlreadyApproachedChange = (value) => {
    setAlreadyApproached(value);
    alreadyApproachedRef.current = value;
    setCurrentPage(0);
    if (hasStartedSearchRef.current) fetchResults(0, searchQuery);
  };

  // A mark changes which rows each filter shows, so every cached page is stale.
  const handleApproachedChange = () => {
    dataCache.current.clear();
  };

  // Trigger validation when search starts (handles first click via handleStartSearch)
  const prevHasStartedSearch = useRef(hasStartedSearch);
  useEffect(() => {
    if (hasStartedSearch && !prevHasStartedSearch.current && selectedCountry) {
      triggerValidation();
    }
    prevHasStartedSearch.current = hasStartedSearch;
  }, [selectedCountry, hasStartedSearch]);

  // Cycle through loading messages during page navigation so user sees activity
  useEffect(() => {
    if (!isLoadingTableData) {
      setPaginationLoadingMessage('');
      return;
    }

    const brandName = brandData.brand_name || brandData.brand_url || 'your brand';
    const messages = [
      `Loading results for ${brandName}...`,
      `Fetching validated affiliates for ${brandName}...`,
    ];

    let index = 0;
    setPaginationLoadingMessage(messages[0]);

    const interval = setInterval(() => {
      index = (index + 1) % messages.length;
      setPaginationLoadingMessage(messages[index]);
    }, 4000);

    return () => clearInterval(interval);
  }, [isLoadingTableData, brandData.brand_name, brandData.brand_url]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (validationPollRef.current) {
        clearInterval(validationPollRef.current);
        validationPollRef.current = null;
      }
    };
  }, []);

  // Pagination info for UniversalTable
  const paginationInfo = {
    currentPage: currentPage,
    page: currentPage,
    totalPages: totalPages,
    totalItems: totalItems,
    total: totalItems,
    pageSize: PAGE_SIZE,
    hasNextPage: currentPage + 1 < totalPages,
    hasPreviousPage: currentPage > 0
  };

  // Memoize filtered data to prevent unnecessary re-renders
  const filteredData = useMemo(() => affiliates, [affiliates]);

  // Handle deleting a prospect
  const handleDeleteProspect = async (prospect) => {
    if (!prospect?.id) return;
    try {
      const response = await fetch(`${basePath}/api/affiliate-recruitment/prospect/${prospect.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (data.success) {
        setAffiliates(prev => prev.filter(item => item.id !== prospect.id));
      } else {
        alert(data.message || 'Failed to delete prospect');
      }
    } catch (error) {
      console.error('Error deleting prospect:', error);
      alert('Failed to delete prospect');
    }
  };

  // Separator strip: shown once when transitioning from relevant (tier 0-2) to other results (tier 3+)
  const activeVerticalLabel = (selectedVertical || brandData.vertical || 'casino').replace(/_/g, ' ');
  const renderBeforeRow = useCallback((rowData, rowIdx, allRows) => {
    const currTier = rowData.priority_tier ?? 0;
    if (currTier < 3) return null; // Row is relevant — no strip needed

    // Show strip before the FIRST tier 3+ row on this page
    const isFirstIrrelevant = rowIdx === 0
      || (allRows[rowIdx - 1]?.original?.priority_tier ?? 0) <= 2;
    if (!isFirstIrrelevant) return null;

    const isStillValidating = validationJobStatus?.isRunning && !validationJobStatus?.isComplete;

    return (
      <tr>
        <td colSpan={100} className="px-0 py-0">
          <div className="flex items-center justify-between gap-3 px-6 py-3 bg-white dark:bg-slate-800 border-y border-gray-200 dark:border-slate-700">
            <div className="flex items-center gap-3">
              {isStillValidating ? (
                <Loader2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400 flex-shrink-0 animate-spin" />
              ) : (
                <Info className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
              )}
              <span className="text-sm text-gray-600 dark:text-slate-400">
                {isStillValidating ? (
                  <>
                    Still checking for <span className="font-semibold">{activeVerticalLabel}</span> affiliates — {validationJobStatus.approved} relevant found so far, more may appear above.
                  </>
                ) : (
                  <>
                    The results below are not specifically <span className="font-semibold">{selectedCountry?.toUpperCase() || ''}</span> affiliates, but they are still getting traffic from <span className="font-semibold">{selectedCountry?.toUpperCase() || 'your selected market'}</span> and can be useful to get traffic.
                  </>
                )}
              </span>
            </div>
          </div>
        </td>
      </tr>
    );
  }, [activeVerticalLabel, validationJobStatus]);

  // Helper functions
  const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
    return num?.toLocaleString() || '0';
  };

  const getProcessedGeos = (geoData) => {
    if (!geoData) return [];
    const excludedCountries = ['unknown', 'xx', 'mobile'];
    const trimGeo = geoData.split(',');
    return trimGeo
      .map((trimgeo) => {
        const [country, count] = trimgeo.split(':');
        const trimmedCountry = country?.trim();
        if (!trimmedCountry || excludedCountries.includes(trimmedCountry.toLowerCase())) return null;
        const shortCountryName = countryList(trimmedCountry) || trimmedCountry;
        if (excludedCountries.includes(shortCountryName.toLowerCase())) return null;
        return { country: shortCountryName, count: count?.trim() || '0' };
      })
      .filter(Boolean);
  };

  const getUniqueKeywords = (keywordString) => {
    if (!keywordString) return [];
    const keywords = keywordString.split(',').map(keyword => keyword.trim().toLowerCase()).filter(keyword => keyword.length > 0);
    const uniqueKeywords = [...new Set(keywords)];
    return uniqueKeywords.map(uniqueKeyword => {
      const originalKeyword = keywordString.split(',').map(k => k.trim()).find(k => k.toLowerCase() === uniqueKeyword);
      return originalKeyword;
    }).filter(Boolean);
  };

  const formatUrl = (url) => {
    if (!url) return '#';
    if (url.startsWith('http://') || url.startsWith('https://')) return url;
    return `https://${url}`;
  };

  // Custom render function for expanded row content
  const renderExpandedRow = (rowData) => {
    // Check if we have scanned pages to show in Top Pages (to avoid duplicate sections)
    const hasScannedPagesForTopPages = (rowData.scanned_pages?.length > 0) ||
      (rowData.affiliate_url_confidence?.split(',').filter(url => url.trim().startsWith('http')).length > 0);

    return (
      <div className="p-6 space-y-6 bg-gradient-to-b from-gray-50 to-white dark:from-slate-900/50 dark:to-slate-900">
        {/* Country Traffic Breakdown */}
        <div>
          <h4 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3">
            Traffic by Country
          </h4>
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
            {rowData.top_geo_organic_traffic ? (
              <div className="grid grid-cols-4">
                {getProcessedGeos(rowData.top_geo_organic_traffic).map((geo, index) => {
                  const isRelevant = rowData.relevant_countries?.includes(geo.country) ||
                                     rowData.relevant_countries?.includes(geo.country.toUpperCase());
                  return (
                    <div
                      key={index}
                      className={`group relative flex items-center justify-between p-4 border-b border-r border-gray-50 dark:border-slate-700 last:border-r-0 hover:bg-gray-50 dark:hover:bg-slate-750 transition-colors ${
                        isRelevant ? 'bg-emerald-50/50 dark:bg-emerald-900/10' : ''
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CountryFlag countryCode={geo.country} className="w-6 h-4 rounded shadow-sm" />
                        <span className="text-sm font-medium text-gray-700 dark:text-slate-300">{geo.country}</span>
                        {isRelevant && (
                          <span className="w-2 h-2 rounded-full bg-emerald-500" title="Content targets this market" />
                        )}
                      </div>
                      <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatNumber(geo.count)}</span>

                      {/* Hover tooltip */}
                      <div className="absolute left-0 top-full mt-1 z-[9999] hidden group-hover:block w-56 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-xl">
                        {isRelevant ? (
                          <div className="flex items-center gap-2 text-emerald-400">
                            <CheckCircle className="w-3 h-3" />
                            <span>Content targets {geo.country}</span>
                          </div>
                        ) : (
                          <div className="text-gray-400">
                            Traffic from {geo.country} but content not specifically targeted
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="p-8 text-center text-sm text-gray-400 dark:text-slate-500">
                No country data available
              </div>
            )}
          </div>
        </div>

        {/* Two Column Layout for Pages and Keywords */}
        <div className="grid grid-cols-2 gap-6">
          {/* Top Pages - Show AI verified first, then other pages */}
          <div>
            {(() => {
              // Read scanned pages — supports new per-country+vertical format { "US:casino": [...] } and old formats
              const rawPages = rowData.scanned_pages || [];
              let scannedPages = [];
              const sc = selectedCountry?.toUpperCase();
              const sv = (selectedVertical || brandData.vertical || 'casino').toLowerCase().replace(/[_\s]+/g, '').trim();
              if (rawPages && !Array.isArray(rawPages) && typeof rawPages === 'object') {
                // Try vertical-specific key first (e.g. "US:casino")
                if (sc && sv && rawPages[`${sc}:${sv}`]) {
                  scannedPages = rawPages[`${sc}:${sv}`];
                } else if (sc && sv === 'casino' && rawPages[sc]) {
                  // Fallback: old country-only key (old data was casino)
                  scannedPages = rawPages[sc];
                } else if (sc) {
                  // Fallback: any available vertical for the same country
                  for (const key of Object.keys(rawPages)) {
                    if (key.startsWith(`${sc}:`) && Array.isArray(rawPages[key]) && rawPages[key].length > 0) {
                      scannedPages = rawPages[key];
                      break;
                    }
                  }
                }
              } else if (Array.isArray(rawPages)) {
                // Legacy array format — filter by country
                scannedPages = rawPages.filter(page => {
                  if (!page.relevantCountries || page.relevantCountries.length === 0) return true;
                  if (!sc) return true;
                  return page.relevantCountries.some(rc => rc.toUpperCase() === sc);
                });
              }

              // Fallback: if no scanned pages but validated_page URL exists, show it with green checkmark
              if (scannedPages.length === 0) {
                const validatedUrl = rowData.validated_page || '';
                if (validatedUrl && validatedUrl.startsWith('http')) {
                  scannedPages = [{
                    url: validatedUrl,
                    siteType: rowData.site_type || 'affiliate',
                    isAffiliate: true,
                    isRelevant: true,
                    relevantCountries: rowData.relevant_countries || [sc],
                    confidence: rowData.validation_confidence || 0.8,
                    reason: 'AI verified page'
                  }];
                }
              }
              const hasScannedPages = scannedPages.length > 0;

              // Get other pages from top_page, excluding URLs already in scanned pages + deduplicate
              const scannedUrls = new Set(scannedPages.map(p => p.url?.toLowerCase()));
              const seenUrls = new Set();
              const otherPages = rowData.top_page
                ? rowData.top_page.split(',')
                    .map(url => url.trim())
                    .filter(url => {
                      const lower = url.toLowerCase();
                      if (url.length === 0 || scannedUrls.has(lower) || seenUrls.has(lower)) return false;
                      seenUrls.add(lower);
                      return true;
                    })
                    .slice(0, 5)
                : [];
              const hasOtherPages = otherPages.length > 0;

              return (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                      Top Pages
                    </h4>
                  </div>
                  <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
                    <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-64 overflow-y-auto">
                      {/* AI Verified Pages - Most relevant for target country */}
                      {hasScannedPages && scannedPages.map((page, index) => (
                        <div key={`scanned-${index}`} className="group flex items-center gap-3 px-4 py-3 hover:bg-emerald-50/50 dark:hover:bg-emerald-900/10 transition-colors bg-emerald-50/30 dark:bg-emerald-900/5">
                          <span className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 bg-emerald-100 dark:bg-emerald-900/40">
                            <CheckCircle className="w-3 h-3 text-emerald-600 dark:text-emerald-400" />
                          </span>
                          <div className="flex-1 min-w-0">
                            <a
                              href={page.url?.startsWith('http') ? page.url : `https://${page.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-gray-700 dark:text-slate-300 truncate block group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors"
                            >
                              {page.url}
                            </a>
                            <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                              Relevant for {page.relevantCountries?.join(', ') || page.countryCode || 'target market'}
                            </span>
                          </div>
                          {/* Info icon with relevance tooltip - hide if reason contains error, temporarily unavailable, or is AI verified */}
                          {page.reason && !page.reason.toLowerCase().includes('error') && !page.reason.toLowerCase().includes('temporarily unavailable') && !page.reason.toLowerCase().includes('ai verified') && (
                            <div className="relative flex-shrink-0 group/tooltip">
                              <Info className="w-4 h-4 text-emerald-500 dark:text-emerald-400 hover:text-emerald-600 cursor-help" />
                              <div
                                className="fixed z-[99999] invisible group-hover/tooltip:visible opacity-0 group-hover/tooltip:opacity-100 pointer-events-none group-hover/tooltip:pointer-events-auto transition-all w-80 p-4 bg-gray-900 text-white text-xs rounded-lg shadow-2xl border border-gray-700"
                                style={{
                                  top: '50%',
                                  left: '50%',
                                  transform: 'translate(-50%, -50%)'
                                }}
                              >
                                <div className="flex items-center gap-2 mb-2">
                                  <Sparkles className="w-3.5 h-3.5 text-emerald-400" />
                                  <span className="font-medium text-emerald-400">Why Relevant</span>
                                </div>
                                <p className="text-gray-300 leading-relaxed">
                                  {page.reason.toLowerCase().includes('not relevant')
                                    ? `Not relevant for ${page.relevantCountries?.join(', ') || 'target market'}, but could still be a good catch for driving traffic.`
                                    : page.reason
                                  }
                                </p>
                                {page.relevantCountries?.length > 0 && (
                                  <div className="mt-2 pt-2 border-t border-gray-700 flex flex-wrap gap-1">
                                    <span className="text-gray-500 text-xs">Targets:</span>
                                    {page.relevantCountries.map((country, i) => (
                                      <span key={i} className="text-xs bg-emerald-900/60 text-emerald-300 px-1.5 py-0.5 rounded">
                                        {country}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                          <a
                            href={page.url?.startsWith('http') ? page.url : `https://${page.url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity hover:text-emerald-600"
                          >
                            <ExternalLink className="w-3.5 h-3.5 text-emerald-400 dark:text-emerald-500" />
                          </a>
                        </div>
                      ))}

                      {/* Separator if both sections exist */}
                      {hasScannedPages && hasOtherPages && (
                        <div className="px-4 py-2 bg-gray-50 dark:bg-slate-700/50">
                          <span className="text-xs text-gray-400 dark:text-slate-500 font-medium">Other Pages</span>
                        </div>
                      )}

                      {/* Other pages from top_page */}
                      {hasOtherPages && otherPages.map((page, index) => (
                        <a
                          key={`other-${index}`}
                          href={formatUrl(page)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-slate-750 transition-colors group"
                        >
                          <span className="w-5 h-5 rounded-md bg-gray-100 dark:bg-slate-700 flex items-center justify-center text-xs font-medium text-gray-500 dark:text-slate-400">
                            {index + 1}
                          </span>
                          <span className="flex-1 min-w-0 text-sm text-gray-600 dark:text-slate-400 truncate group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                            {page}
                          </span>
                          <ExternalLink className="w-3.5 h-3.5 text-gray-300 dark:text-slate-600 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </a>
                      ))}

                      {/* No pages at all */}
                      {!hasScannedPages && !hasOtherPages && (
                        <div className="p-8 text-center text-sm text-gray-400 dark:text-slate-500">No top pages available</div>
                      )}
                    </div>
                  </div>
                </>
              );
            })()}
          </div>

          {/* Top Organic Keywords */}
          <div>
            <h4 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3">
              Top Keywords
            </h4>
            <div className="bg-white dark:bg-slate-800 rounded-xl border border-gray-100 dark:border-slate-700 overflow-hidden">
              {rowData.keywords ? (
                <div className="divide-y divide-gray-50 dark:divide-slate-700 max-h-56 overflow-y-auto">
                  {getUniqueKeywords(rowData.keywords).slice(0, 8).map((keyword, index) => (
                    <div key={index} className="flex items-center justify-between px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span className="w-6 h-6 rounded-md bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center text-xs font-medium text-emerald-600 dark:text-emerald-400">
                          {index + 1}
                        </span>
                        <span className="text-sm text-gray-700 dark:text-slate-300">{keyword}</span>
                      </div>
                      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">organic</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 text-center text-sm text-gray-400 dark:text-slate-500">No keywords available</div>
              )}
            </div>
          </div>
        </div>

        {/* Paid Keywords */}
        {rowData.paid_keyword && (
          <div>
            <h4 className="text-xs font-semibold text-gray-400 dark:text-slate-500 uppercase tracking-wider mb-3">
              Paid Keywords
            </h4>
            <div className="flex flex-wrap gap-2">
              {getUniqueKeywords(rowData.paid_keyword).slice(0, 12).map((keyword, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 border border-purple-100 dark:border-purple-800 rounded-lg text-sm text-purple-700 dark:text-purple-300"
                >
                  {keyword}
                  <span className="text-xs text-purple-400 dark:text-purple-500">paid</span>
                </span>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center">
        <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-gray-100 dark:border-slate-800 max-w-md w-full">
          <ValidationProgressBar
            progress={loadingProgress}
            message={loadingMessage}
            validationInfo={validationJobStatus}
          />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-slate-950 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 shadow-xl max-w-md w-full p-8">
          <div className="flex flex-col items-center text-center">
            <div className="w-14 h-14 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-5">
              <AlertCircle className="w-7 h-7 text-red-600 dark:text-red-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Unable to Load Data</h3>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">{error}</p>
            {brandData.brand_url ? (
              <button
                onClick={() => window.location.reload()}
                className="w-full px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                Try Again
              </button>
            ) : (
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2.5 bg-gray-900 dark:bg-white text-white dark:text-gray-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-gray-100 transition-colors"
              >
                Go to Dashboard
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 min-h-screen bg-gray-50 dark:bg-slate-950">

      {/* Header section */}
      <div className="mb-6 space-y-4">
        {/* Top bar - Brand info and back button */}
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm">
          <div className="px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Brand Info */}
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center overflow-hidden">
                  {brandData.brand_url ? (
                    <img
                      src={`https://www.google.com/s2/favicons?domain=${brandData.brand_url}&sz=48`}
                      alt={brandData.brand_name || 'Brand'}
                      className="w-8 h-8"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <Globe className={`w-6 h-6 text-gray-400 dark:text-slate-500 ${brandData.brand_url ? 'hidden' : ''}`} />
                </div>
                <div>
                  <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Untapped Opportunities
                  </h1>
                  <div className="flex items-center gap-2 mt-1">
                    {brandData.brand_name && (
                      <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">
                        {brandData.brand_name.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                      </span>
                    )}
                    {brandData.brand_url && (
                      <>
                        <span className="text-gray-300 dark:text-slate-600">•</span>
                        <span className="text-sm text-gray-500 dark:text-slate-400">
                          {brandData.brand_url}
                        </span>
                      </>
                    )}
                    {brandData.vertical && (
                      <>
                        <span className="text-gray-300 dark:text-slate-600">•</span>
                        <span className="text-xs bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 px-2 py-0.5 rounded-full capitalize font-medium">
                          {brandData.vertical}
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Back Button */}
              <button
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white bg-gray-100 dark:bg-slate-800 hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-all"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back</span>
              </button>
            </div>
          </div>
        </div>

        {/* Country selector bar */}
        {availableCountries.length > 0 && (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-800 shadow-sm">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between gap-6">
                {/* Country Selection */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                    <Flag className="w-4 h-4" />
                    <span className="text-sm font-medium">Target Country</span>
                  </div>

                  <div className="flex items-center gap-3">
                    {/* Country Flag */}
                    {selectedCountry && (
                      <div className="flex items-center justify-center w-10 h-7 rounded border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 overflow-hidden">
                        <CountryFlag countryCode={selectedCountry} className="w-7 h-5" />
                      </div>
                    )}

                    {/* Country Dropdown */}
                    <div className="relative">
                      <select
                        value={selectedCountry}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        disabled={loading || isLoadingTableData}
                        className="appearance-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg py-2.5 pl-4 pr-10 text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                      >
                        {availableCountries.map((country) => (
                          <option key={country} value={country}>
                            {getCountryName(country)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>

                    {/* Countries count badge */}
                    {availableCountries.length > 1 && (
                      <span className="text-xs text-gray-500 dark:text-slate-500 bg-gray-100 dark:bg-slate-800 px-2.5 py-1 rounded-full border border-gray-200 dark:border-slate-700">
                        {availableCountries.length} available
                      </span>
                    )}
                  </div>
                </div>

                {/* Vertical Selection - only show if brand has multiple verticals */}
                {availableVerticals.length > 1 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      <span className="text-sm font-medium">Vertical</span>
                    </div>

                    <div className="relative">
                      <select
                        value={selectedVertical}
                        onChange={(e) => handleVerticalChange(e.target.value)}
                        disabled={loading || isLoadingTableData}
                        className="appearance-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg py-2.5 pl-4 pr-10 text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px] capitalize"
                      >
                        {availableVerticals.map((v) => (
                          <option key={v} value={v} className="capitalize">
                            {v.charAt(0).toUpperCase() + v.slice(1)}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {/* Sub-category selection — the second level under the vertical.
                    Only Trading & Investment brands carry sub-categories, so
                    this box simply does not render for a gambling brand. */}
                {availableSubVerticals.length > 0 && (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                      <Layers className="w-4 h-4" />
                      <span className="text-sm font-medium">Sub-category</span>
                    </div>

                    <div className="relative">
                      <select
                        value={selectedSubVertical}
                        onChange={(e) => handleSubVerticalChange(e.target.value)}
                        disabled={loading || isLoadingTableData}
                        className="appearance-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg py-2.5 pl-4 pr-10 text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[180px]"
                      >
                        {/* '' keeps the whole vertical pool — the same number the
                            dashboard card shows. */}
                        <option value="">All sub-categories</option>
                        {availableSubVerticals.map((sv) => (
                          <option key={sv} value={sv}>{sv}</option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-slate-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                    </div>
                  </div>
                )}

                {/* Already Approached: No hides the affiliates you approached, Yes shows only them. */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-slate-400">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Already Approached</span>
                  </div>

                  <div className="relative">
                    <select
                      value={alreadyApproached}
                      onChange={(e) => handleAlreadyApproachedChange(e.target.value)}
                      disabled={loading || isLoadingTableData}
                      className="appearance-none bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg py-2.5 pl-4 pr-10 text-sm font-medium text-gray-900 dark:text-white cursor-pointer hover:border-emerald-500 dark:hover:border-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all disabled:opacity-50 disabled:cursor-not-allowed min-w-[90px]"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 dark:text-slate-500">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Find Opportunities Button */}
                <button
                  onClick={handleStartSearch}
                  disabled={loading || isLoadingTableData || !selectedCountry}
                  className="flex items-center gap-2 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                >
                  {loading || isLoadingTableData ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Searching...</span>
                    </>
                  ) : hasStartedSearch ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      <span>Refresh Results</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4" />
                      <span>Find Opportunities</span>
                    </>
                  )}
                </button>

                {/* Admin-only: Re-scan button */}
                {user?.businessEmail?.toLowerCase() === 'mars.mediacle@gmail.com' && hasStartedSearch && (
                  <button
                    onClick={handleRescan}
                    disabled={loading || isLoadingTableData || !selectedCountry}
                    className="flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-sm hover:shadow-md"
                    title="Clear cache and re-scan all affiliates from scratch"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    <span>Re-scan</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Results section */}
      {affiliates.length > 0 || isLoadingTableData ? (
        <>
          <UniversalTable
            data={filteredData}
            paginationInfo={paginationInfo}
            onFetchData={handlePageChange}
            searchable={false}
            loading={isLoadingTableData}
            loadingMessage={isLoadingTableData ? paginationLoadingMessage : null}
            userPlan={userPlan}
            expandable={true}
            renderExpandedRow={renderExpandedRow}
            singleExpand={true}
            hidePageSize={true}
            renderBeforeRow={renderBeforeRow}
            onDelete={handleDeleteProspect}
            // Same rule as the Live Affiliate table: this list is scoped to one
            // brand, so the Vertical column shows that brand's vertical rather
            // than every vertical the site carries.
            brandVertical={brandData.vertical}
            approachable
            onApproachedChange={handleApproachedChange}
          />

          {/* Background validation progress — shown while running, paused, or complete */}
          {validationJobStatus && validationJobStatus.isRunning && (
            <div className="mt-3 px-4 py-3 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <Loader2 className="w-4 h-4 text-emerald-500 animate-spin flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-700 dark:text-slate-300">
                    Validating affiliates in background... {validationJobStatus.validated}/{validationJobStatus.totalCandidates} checked, {validationJobStatus.approved} relevant found
                  </p>
                  <div className="mt-1.5 w-full bg-gray-100 dark:bg-slate-800 rounded-full h-1.5 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-emerald-500 to-teal-500 h-1.5 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${validationJobStatus.totalCandidates > 0 ? Math.round((validationJobStatus.validated / validationJobStatus.totalCandidates) * 100) : 0}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-900/30 px-2.5 py-1 rounded-full flex-shrink-0">
                  {validationJobStatus.totalCandidates > 0 ? Math.round((validationJobStatus.validated / validationJobStatus.totalCandidates) * 100) : 0}%
                </span>
              </div>
            </div>
          )}

          {/* Paused — show status (auto-loads more when user browses to boundary) */}
          {validationJobStatus && validationJobStatus.isPaused && validationJobStatus.hasMoreCandidates && (
            <div className="mt-3 px-4 py-3 bg-white dark:bg-slate-900 rounded-lg border border-emerald-200 dark:border-emerald-800/40">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                <p className="text-sm text-gray-700 dark:text-slate-300">
                  Found <span className="font-semibold text-emerald-600 dark:text-emerald-400">{validationJobStatus.approved} relevant</span> out of {validationJobStatus.validated} checked — more will load automatically as you browse
                </p>
              </div>
            </div>
          )}

          {/* Complete — all candidates checked */}
          {validationJobStatus && validationJobStatus.isComplete && (
            <div className="mt-3 px-4 py-3 bg-white dark:bg-slate-900 rounded-lg border border-gray-200 dark:border-slate-800">
              <div className="flex items-center gap-3">
                <CheckCircle className="w-4 h-4 text-gray-400 dark:text-slate-500 flex-shrink-0" />
                <p className="text-sm text-gray-500 dark:text-slate-400">
                  All {validationJobStatus.totalCandidates} candidates checked — {validationJobStatus.approved} relevant found
                </p>
              </div>
            </div>
          )}
        </>
      ) : !hasStartedSearch ? (
        /* Welcome message - user hasn't started search yet */
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm">
          <div className="flex flex-col items-center justify-center py-20 px-8">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border border-emerald-100 dark:border-emerald-800/30 flex items-center justify-center mb-8">
              <Sparkles className="w-10 h-10 text-emerald-500 dark:text-emerald-400" />
            </div>
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-3">
              Ready to Find Opportunities
            </h3>
            <p className="text-base text-gray-500 dark:text-slate-400 text-center max-w-lg mb-8 leading-relaxed">
              Select a target country from the dropdown above and click <span className="font-semibold text-emerald-600 dark:text-emerald-400">"Find Opportunities"</span> to discover untapped affiliate sites for {brandData.brand_name || 'your brand'}.
            </p>
            {selectedCountry && (
              <div className="flex items-center gap-4 bg-gray-50 dark:bg-slate-800/50 px-6 py-4 rounded-xl border border-gray-200 dark:border-slate-700">
                <div className="w-12 h-9 rounded-lg border border-gray-200 dark:border-slate-600 overflow-hidden flex items-center justify-center bg-white dark:bg-slate-800">
                  <CountryFlag countryCode={selectedCountry} className="w-8 h-6" />
                </div>
                <div>
                  <p className="text-xs text-gray-400 dark:text-slate-500 uppercase tracking-wider font-medium">Selected Country</p>
                  <p className="text-lg font-semibold text-gray-900 dark:text-white">{getCountryName(selectedCountry)}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* No results found after search */
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-sm">
          <div className="flex flex-col items-center justify-center py-20 px-8">
            <div className="w-16 h-16 rounded-2xl bg-gray-100 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 flex items-center justify-center mb-6">
              <AlertCircle className="w-8 h-8 text-gray-400 dark:text-slate-500" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No Verified Affiliates</h3>
            <p className="text-base text-gray-500 dark:text-slate-400 text-center max-w-md mb-8">
              No affiliates passed AI verification for <span className="font-medium text-gray-700 dark:text-slate-300">{getCountryName(selectedCountry)}</span>. Try selecting a different country.
            </p>
            <button
              onClick={() => navigate('/dashboard')}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-gray-900 dark:bg-slate-100 text-white dark:text-gray-900 rounded-xl font-medium hover:bg-gray-800 dark:hover:bg-white transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SmartTable;
